import { useEffect, useRef, useState } from "react";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Image,
  Italic,
  Link,
  Save,
  Underline,
} from "lucide-react";
import { Button, Checkbox } from "react-aria-components";
import { getCsrf } from "../api";
import { UnifiedSelect } from "@tellem/ui";

type Account = {
  id: string;
  alias: string;
  email: string;
  status: string;
  provider?: string;
};
type Signature = { html_content: string; enabled: boolean };

export function SignatureManager({
  projectId,
  accounts,
  compact = false,
}: {
  projectId: string;
  accounts: Account[];
  compact?: boolean;
}) {
  const preferredAccount =
    accounts.find((account) => account.provider !== "lark") ?? accounts[0];
  const preferredAccountId = preferredAccount?.id ?? "";
  const [accountId, setAccountId] = useState(preferredAccountId);
  const [targetAccountIds, setTargetAccountIds] = useState<Set<string>>(
    () => new Set(preferredAccountId ? [preferredAccountId] : []),
  );
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkHref, setLinkHref] = useState("https://");
  const [font, setFont] = useState("Arial");
  const [fontSize, setFontSize] = useState("3");
  const editorRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const loadedRef = useRef("");
  const dirtyRef = useRef(false);
  const savedRangeRef = useRef<Range | null>(null);
  useEffect(() => {
    if (!accounts.some((item) => item.id === accountId))
      setAccountId(preferredAccountId);
  }, [accounts, accountId, preferredAccountId]);
  useEffect(() => {
    setTargetAccountIds(new Set(accountId ? [accountId] : []));
  }, [accountId]);
  useEffect(() => {
    if (!accountId) return;
    loadedRef.current = "";
    dirtyRef.current = false;
    void fetch(`/api/projects/${projectId}/gmail/${accountId}/signature`)
      .then((r) => r.json())
      .then((data: { signature: Signature | null }) => {
        const html = data.signature?.html_content ?? "";
        setContent(html);
        if (editorRef.current) editorRef.current.innerHTML = html;
        loadedRef.current = accountId;
      });
  }, [projectId, accountId]);
  const persist = async (
    html = content,
    showNotice = true,
    targets: string[] = accountId ? [accountId] : [],
  ) => {
    const csrf = await getCsrf();
    if (!csrf || !accountId || !targets.length) return;
    setBusy(true);
    try {
      const results = await Promise.all(
        targets.map(async (targetAccountId) => {
          const response = await fetch(
            `/api/projects/${projectId}/gmail/${targetAccountId}/signature`,
            {
              method: "PUT",
              headers: {
                "content-type": "application/json",
                "x-csrf-token": csrf,
              },
              body: JSON.stringify({ htmlContent: html, enabled: true }),
            },
          );
          const result = (await response.json()) as { htmlContent?: string };
          if (!response.ok) throw new Error("Не сохранено");
          return { targetAccountId, result };
        }),
      );
      dirtyRef.current = false;
      const currentResult = results.find(
        (item) => item.targetAccountId === accountId,
      )?.result;
      if (
        currentResult?.htmlContent !== undefined &&
        editorRef.current &&
        currentResult.htmlContent !== html
      ) {
        editorRef.current.innerHTML = currentResult.htmlContent;
        setContent(currentResult.htmlContent);
      }
      if (showNotice)
        setNotice(
          targets.length === 1 ? "Сохранено" : `Сохранено: ${targets.length}`,
        );
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Ошибка");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    if (!dirtyRef.current || loadedRef.current !== accountId) return;
    const timer = setTimeout(() => void persist(content, false), 900);
    return () => clearTimeout(timer);
  }, [content, accountId]);
  const changed = () => {
    const html = editorRef.current?.innerHTML ?? "";
    dirtyRef.current = true;
    setContent(html);
    setNotice("");
  };
  const command = (name: string, value?: string) => {
    editorRef.current?.focus();
    document.execCommand(name, false, value);
    changed();
  };
  const openLink = () => {
    const selection = window.getSelection();
    savedRangeRef.current = selection?.rangeCount
      ? selection.getRangeAt(0).cloneRange()
      : null;
    setLinkOpen(true);
  };
  const addLink = () => {
    if (!/^https?:\/\//i.test(linkHref)) return;
    editorRef.current?.focus();
    const selection = window.getSelection();
    selection?.removeAllRanges();
    if (savedRangeRef.current) selection?.addRange(savedRangeRef.current);
    document.execCommand("createLink", false, linkHref);
    changed();
    setLinkOpen(false);
    setLinkHref("https://");
  };
  const uploadImage = async (file: File) => {
    const csrf = await getCsrf();
    if (!csrf) return;
    const form = new FormData();
    form.set("file", file);
    setBusy(true);
    try {
      const response = await fetch(`/api/projects/${projectId}/assets`, {
        method: "POST",
        headers: { "x-csrf-token": csrf },
        body: form,
      });
      const asset = (await response.json()) as { id?: string; error?: string };
      if (!response.ok || !asset.id)
        throw new Error(asset.error ?? "Ошибка изображения");
      editorRef.current?.focus();
      document.execCommand(
        "insertHTML",
        false,
        `<img src="/api/projects/${projectId}/assets/${asset.id}/content" data-tellem-asset-id="${asset.id}" alt="" style="max-width:180px;height:auto;margin:4px 0">`,
      );
      changed();
      await persist(editorRef.current?.innerHTML ?? "", false);
      setNotice("Изображение сохранено");
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Ошибка");
    } finally {
      setBusy(false);
    }
  };
  if (!accounts.length) return null;
  return (
    <article className={`signature-manager${compact ? " compact" : ""}`}>
      <header>
        <div>
          <Image size={17} />
          <h2>Подпись</h2>
        </div>
        <UnifiedSelect
          ariaLabel="Почтовый ящик"
          className="signature-account-select"
          value={accountId}
          onValueChange={setAccountId}
          options={accounts.map((account) => ({
            value: account.id,
            label: account.email,
          }))}
        />
      </header>
      <section className="signature-targets" aria-label="Ящики с подписью">
        <header>
          <strong>Применить к</strong>
          <span>{targetAccountIds.size} выбрано</span>
          <Button
            onPress={() =>
              setTargetAccountIds(
                new Set(accounts.map((account) => account.id)),
              )
            }
          >
            Все
          </Button>
          <Button onPress={() => setTargetAccountIds(new Set())}>
            Очистить
          </Button>
        </header>
        <div className="signature-target-grid">
          {accounts.map((account) => (
            <Checkbox
              key={account.id}
              isSelected={targetAccountIds.has(account.id)}
              onChange={(checked) => {
                setTargetAccountIds((current) => {
                  const next = new Set(current);
                  if (checked) next.add(account.id);
                  else next.delete(account.id);
                  return next;
                });
              }}
            >
              <span aria-hidden="true" />
              <strong>{account.email}</strong>
            </Checkbox>
          ))}
        </div>
      </section>
      <div className="signature-editor">
        <div
          className="signature-toolbar"
          role="toolbar"
          aria-label="Форматирование подписи"
        >
          <UnifiedSelect
            ariaLabel="Шрифт"
            className="signature-font-select"
            popoverClassName="signature-toolbar-popover"
            value={font}
            onValueChange={(value) => {
              setFont(value);
              command("fontName", value);
            }}
            options={["Arial", "Georgia", "Verdana", "Tahoma"].map((value) => ({
              value,
              label: value,
            }))}
          />
          <UnifiedSelect
            ariaLabel="Размер"
            className="signature-size-select"
            popoverClassName="signature-toolbar-popover"
            value={fontSize}
            onValueChange={(value) => {
              setFontSize(value);
              command("fontSize", value);
            }}
            options={[
              { value: "2", label: "12" },
              { value: "3", label: "14" },
              { value: "4", label: "18" },
              { value: "5", label: "24" },
            ]}
          />
          <button
            className="signature-command"
            type="button"
            title="Жирный"
            onClick={() => command("bold")}
          >
            <Bold size={16} />
          </button>
          <button
            className="signature-command"
            type="button"
            title="Курсив"
            onClick={() => command("italic")}
          >
            <Italic size={16} />
          </button>
          <button
            className="signature-command"
            type="button"
            title="Подчёркивание"
            onClick={() => command("underline")}
          >
            <Underline size={16} />
          </button>
          <label className="signature-color" title="Цвет">
            <input
              type="color"
              defaultValue="#171717"
              onChange={(event) => command("foreColor", event.target.value)}
            />
          </label>
          <button
            className="signature-command"
            type="button"
            title="По левому краю"
            onClick={() => command("justifyLeft")}
          >
            <AlignLeft size={16} />
          </button>
          <button
            className="signature-command"
            type="button"
            title="По центру"
            onClick={() => command("justifyCenter")}
          >
            <AlignCenter size={16} />
          </button>
          <button
            className="signature-command"
            type="button"
            title="По правому краю"
            onClick={() => command("justifyRight")}
          >
            <AlignRight size={16} />
          </button>
          <button
            className="signature-command"
            type="button"
            title="Ссылка"
            onMouseDown={(event) => event.preventDefault()}
            onClick={openLink}
          >
            <Link size={16} />
          </button>
          {linkOpen && (
            <span className="signature-link-popover">
              <input
                aria-label="Адрес ссылки"
                value={linkHref}
                onChange={(event) => setLinkHref(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    addLink();
                  }
                  if (event.key === "Escape") setLinkOpen(false);
                }}
                autoFocus
              />
              <button type="button" onClick={addLink}>
                Добавить
              </button>
            </span>
          )}
          <button
            className="signature-command"
            type="button"
            title="Изображение"
            onClick={() => imageInputRef.current?.click()}
          >
            <Image size={16} />
          </button>
          <input
            ref={imageInputRef}
            hidden
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void uploadImage(file);
              event.target.value = "";
            }}
          />
        </div>
        <div
          ref={editorRef}
          className="signature-canvas"
          contentEditable
          suppressContentEditableWarning
          data-placeholder="Введите подпись"
          onInput={changed}
          onBlur={() => {
            if (dirtyRef.current)
              void persist(editorRef.current?.innerHTML ?? "", false);
          }}
        />
      </div>
      <footer>
        {notice && <span>{notice}</span>}
        <Button
          className="primary-button"
          onPress={() => void persist(content, true, [...targetAccountIds])}
          isDisabled={busy || targetAccountIds.size === 0}
        >
          <Save size={14} />
          Сохранить для {targetAccountIds.size}
        </Button>
      </footer>
    </article>
  );
}
